// Inventario estancado y capital atado, calculados sobre la lente del catálogo (modo lista).
import { productLabel, type InventoryLensListItem } from '@/api/services/retail-catalog/backoffice';

export interface StagnantSku {
    sku: string;
    label: string;
    stock: number;
    /** Stock × precio vigente; `null` sin precio o en un kit. */
    valueCents: number | null;
    /** El stock del kit sale de sus componentes, que ya suman en su propia fila. */
    isKit: boolean;
}

export interface StagnantInventory {
    /** Valor de todo el stock con precio. */
    tiedCapitalCents: number;
    /** Valor del stock que no se movió en el periodo. */
    stagnantValueCents: number;
    stagnant: StagnantSku[];
    /** SKUs con stock que no suman dinero porque no tienen precio; los kits no cuentan. */
    unpricedSkus: number;
}

function isKit(item: InventoryLensListItem): boolean {
    return item.category === 'BUNDLE';
}

/** Un kit no tiene valor propio: sumarlo contaría dos veces las piezas de sus componentes. */
function valueOf(item: InventoryLensListItem): number | null {
    if (isKit(item) || item.basePriceCents === null) return null;
    return item.stockTotal * item.basePriceCents;
}

/** Porcentaje del capital atado que representa un valor, a un decimal; `null` si no hay base. */
export function capitalSharePct(
    valueCents: number | null,
    tiedCapitalCents: number,
): number | null {
    if (valueCents === null || tiedCapitalCents <= 0) return null;
    return Math.round((valueCents / tiedCapitalCents) * 1000) / 10;
}

/** Estancado = SKU con stock y sin ventas en el periodo. Orden: más valor primero, luego nombre. */
export function buildStagnantInventory(items: InventoryLensListItem[]): StagnantInventory {
    const withStock = items.filter((item) => item.itemType !== 'SERVICE' && item.stockTotal > 0);

    const stagnant = withStock
        .filter((item) => item.soldPeriod === 0)
        .map((item) => ({
            sku: item.sku,
            label: productLabel(item) || item.sku,
            stock: item.stockTotal,
            valueCents: valueOf(item),
            isKit: isKit(item),
        }))
        .sort(
            (a, b) =>
                (b.valueCents ?? -1) - (a.valueCents ?? -1) ||
                a.label.localeCompare(b.label, 'es-MX'),
        );

    const sum = (rows: { valueCents: number | null }[]) =>
        rows.reduce((total, row) => total + (row.valueCents ?? 0), 0);

    return {
        tiedCapitalCents: sum(withStock.map((item) => ({ valueCents: valueOf(item) }))),
        stagnantValueCents: sum(stagnant),
        stagnant,
        unpricedSkus: withStock.filter((item) => !isKit(item) && item.basePriceCents === null)
            .length,
    };
}
